import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Public paths that should NOT redirect to login on 401
    const publicPaths = ['/', '/search', '/register', '/forgot-password', '/login', '/marketplace', '/hosts', '/help', '/how-it-works', '/terms', '/privacy', '/refund-policy', '/grievance', '/about', '/faq', '/contact'];
    const isPublicPage = publicPaths.some((p) => window.location.pathname === p || (p !== '/' && window.location.pathname.startsWith(p)));

    // Handle 401 - attempt token refresh (only on protected pages)
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicPage) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else if (!isPublicPage) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    // Cold-start detection. Azure Container Apps with min-replicas=0
    // return an HTML stop-page with status 404 while the replica boots.
    //
    // We detect this three ways because the body shape depends on
    // axios's response transform (sometimes the HTML body lands as
    // a string, sometimes axios's JSON.parse fallback leaves it
    // empty). Any one of these matching is enough:
    //
    //   * Response Content-Type is text/html on an /api/v1/* path —
    //     our APIs only ever return JSON, so HTML always means the
    //     gateway returned the Azure stop-page (or some other server
    //     error page). Most reliable.
    //   * Body string contains the stop-page signature.
    //   * Body string contains "is stopped or does not exist".
    const respStatus = error.response?.status;
    const respData = error.response?.data;
    const respCT = error.response?.headers?.['content-type'] || '';
    const dataStr = typeof respData === 'string' ? respData : '';
    const isColdStartPage = (respStatus === 404 || respStatus === 503) && (
      respCT.includes('text/html')
      || dataStr.includes('Container App')
      || dataStr.includes('is stopped or does not exist')
    );
    if (isColdStartPage) {
      error.message = 'BACKEND_COLD_START';
      error.isColdStart = true;
      // Toast once per cold-start burst — duplicate suppression keeps
      // the screen quiet when half a dozen requests fail at once.
      if (!window.__coldStartToastShownAt
          || Date.now() - window.__coldStartToastShownAt > 15000) {
        window.__coldStartToastShownAt = Date.now();
        toast.error(
          'Backend is waking up — please retry in 30 seconds.',
          { duration: 6000 },
        );
      }
      return Promise.reject(error);
    }

    // Normalize error message - ALWAYS return a string, never an object
    const extractMessage = (data) => {
      if (!data) return 'Something went wrong';
      if (typeof data.message === 'string') return data.message;
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail)) {
        return data.detail.map((e) => (typeof e === 'string' ? e : e.msg || JSON.stringify(e))).join('; ');
      }
      if (typeof data.detail === 'object') return JSON.stringify(data.detail);
      return 'Something went wrong';
    };

    // Attach string error message to the error object for Redux slices
    if (error.response) {
      error.message = extractMessage(error.response.data);
    }

    // Handle other error statuses
    if (error.response) {
      const { status } = error.response;
      const message = error.message;

      switch (status) {
        case 400:
          toast.error(message);
          break;
        case 403:
          toast.error('You do not have permission to perform this action');
          break;
        case 404:
          // Don't toast for 404s - let the caller handle it
          break;
        case 409:
          toast.error(message);
          break;
        case 422:
          toast.error(message);
          break;
        case 429:
          toast.error('Too many requests. Please wait a moment.');
          break;
        case 500:
        case 502:
        case 503:
          // Silently handle server errors - don't spam toasts
          break;
        default:
          if (status >= 400) toast.error(message);
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

export default api;
